/**
 * XLSX import (PLAN §9 Mode C, §11).
 *
 * An `.xlsx` is a ZIP of XML, so reading one without a dependency means doing
 * three small things rather than one large one: find the parts inside the ZIP,
 * inflate them, and read the handful of elements a spreadsheet of two columns
 * actually uses. That is a few hundred lines against a library that would be
 * the single largest thing in the executable — and `dependencies` stays empty.
 *
 * Inflating uses `DecompressionStream`, which is a platform API in both the
 * browser and Node rather than DOM or I/O, so this file still belongs in
 * `domain/` and still runs identically on both sides. Reading the *file* — the
 * drag-and-drop, the `FileReader` — stays in `web/app.ts`, so the server keeps
 * no upload path at all.
 */

import { pairsFromRows, type TableOutcome, type TableRow } from './table.ts';

export type XlsxProblem = 'not-a-zip' | 'no-worksheet' | 'unsupported-compression';

/** Thrown for a file that is not a workbook this reader can open. */
export class XlsxError extends Error {
  readonly problem: XlsxProblem;

  constructor(problem: XlsxProblem, message: string) {
    super(message);
    this.name = 'XlsxError';
    this.problem = problem;
  }
}

export interface XlsxOutcome extends TableOutcome {
  /** The worksheet the rows came from — the first one, whatever it is called. */
  readonly sheetName: string;
}

/** Reads the first worksheet of a workbook into pairs. */
export async function parseXlsxPairs(bytes: Uint8Array): Promise<XlsxOutcome> {
  const sheet = await readFirstSheet(bytes);
  return { ...pairsFromRows(sheet.rows), sheetName: sheet.name };
}

export interface Worksheet {
  readonly name: string;
  readonly rows: readonly TableRow[];
}

export async function readFirstSheet(bytes: Uint8Array): Promise<Worksheet> {
  const zip = await readZip(bytes);

  const sharedStrings = zip.has('xl/sharedStrings.xml')
    ? readSharedStrings(text(zip.get('xl/sharedStrings.xml') as Uint8Array))
    : [];

  const target = firstSheetPath(zip);
  const rows = readSheet(text(zip.get(target.path) as Uint8Array), sharedStrings);

  return { name: target.name, rows };
}

/* --- The ZIP container ---------------------------------------------------- */

const END_OF_CENTRAL_DIRECTORY = 0x06054b50;
const CENTRAL_FILE_HEADER = 0x02014b50;
const LOCAL_FILE_HEADER = 0x04034b50;

/**
 * Entries are found through the central directory rather than by walking local
 * headers: when a writer streams its output it leaves the sizes in the local
 * header as zero and puts them in a descriptor *after* the data, which makes
 * walking forwards impossible. The central directory always has them.
 */
async function readZip(bytes: Uint8Array): Promise<Map<string, Uint8Array>> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const eocd = findEndOfCentralDirectory(view);

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);

  const files = new Map<string, Uint8Array>();
  for (let entry = 0; entry < count; entry += 1) {
    if (offset + 46 > bytes.length || view.getUint32(offset, true) !== CENTRAL_FILE_HEADER) {
      throw new XlsxError('not-a-zip', 'The file’s directory is damaged.');
    }

    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localOffset = view.getUint32(offset + 42, true);
    const name = text(bytes.subarray(offset + 46, offset + 46 + nameLength));

    files.set(name, await readEntry(bytes, view, localOffset, method, compressedSize));
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return files;
}

function findEndOfCentralDirectory(view: DataView): number {
  // The record is last, but a ZIP comment can follow it — up to 65535 bytes.
  const earliest = Math.max(0, view.byteLength - 22 - 0xffff);
  for (let offset = view.byteLength - 22; offset >= earliest; offset -= 1) {
    if (view.getUint32(offset, true) === END_OF_CENTRAL_DIRECTORY) return offset;
  }
  throw new XlsxError('not-a-zip', 'This is not an Excel workbook.');
}

async function readEntry(
  bytes: Uint8Array,
  view: DataView,
  localOffset: number,
  method: number,
  compressedSize: number,
): Promise<Uint8Array> {
  if (view.getUint32(localOffset, true) !== LOCAL_FILE_HEADER) {
    throw new XlsxError('not-a-zip', 'The file’s contents are damaged.');
  }

  // The local header's own name and extra lengths, which may differ from the
  // central directory's — the extra field commonly does.
  const nameLength = view.getUint16(localOffset + 26, true);
  const extraLength = view.getUint16(localOffset + 28, true);
  const start = localOffset + 30 + nameLength + extraLength;
  const data = bytes.subarray(start, start + compressedSize);

  if (method === 0) return data;
  if (method !== 8) {
    throw new XlsxError(
      'unsupported-compression',
      'This workbook uses a compression this reader does not know.',
    );
  }
  return await inflateRaw(data);
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new DecompressionStream('deflate-raw');
  const decompressed = new Response(
    new Blob([data as unknown as BlobPart]).stream().pipeThrough(stream),
  );
  return new Uint8Array(await decompressed.arrayBuffer());
}

/* --- The XML parts -------------------------------------------------------- */

interface SheetTarget {
  readonly name: string;
  readonly path: string;
}

/**
 * The first sheet in the workbook's own order, which is the order of the tabs —
 * not `sheet1.xml`, whose number is an internal id and need not be the first
 * tab at all.
 */
function firstSheetPath(zip: Map<string, Uint8Array>): SheetTarget {
  const workbook = zip.get('xl/workbook.xml');
  const relationships = zip.get('xl/_rels/workbook.xml.rels');

  if (workbook !== undefined && relationships !== undefined) {
    const sheet = /<sheet\b[^>]*>/.exec(text(workbook));
    const id = sheet === null ? null : attribute(sheet[0], 'r:id');
    const name = sheet === null ? null : attribute(sheet[0], 'name');

    if (id !== null) {
      const pattern = new RegExp(`<Relationship\\b[^>]*Id="${id}"[^>]*>`);
      const match = pattern.exec(text(relationships));
      const target = match === null ? null : attribute(match[0], 'Target');

      if (target !== null) {
        const path = target.startsWith('/')
          ? target.slice(1)
          : `xl/${target.replace(/^\.\//, '')}`;
        if (zip.has(path)) return { name: name ?? 'Sheet1', path };
      }
    }
  }

  // A workbook written by something unusual: fall back to whatever worksheet is
  // in there, rather than refusing a file that plainly has one.
  const fallback = [...zip.keys()]
    .filter((entry) => entry.startsWith('xl/worksheets/') && entry.endsWith('.xml'))
    .sort();
  const path = fallback[0];
  if (path === undefined) throw new XlsxError('no-worksheet', 'This workbook has no sheets.');

  return { name: 'Sheet1', path };
}

/**
 * The shared string table. Each `<si>` may be split into several `<t>` runs
 * when part of the text is formatted differently, and the runs are one value:
 * a phone number in bold from the third digit is still one phone number.
 */
export function readSharedStrings(xml: string): readonly string[] {
  return [...xml.matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)].map((entry) =>
    [...(entry[1] ?? '').matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)]
      .map((run) => decodeXml(run[1] ?? ''))
      .join(''),
  );
}

/**
 * Reads the cells of one worksheet.
 *
 * Columns come from each cell's own reference (`C7`), never from counting the
 * cells present: an empty cell is simply absent from the file, and counting
 * would shift every value after a gap one column to the left.
 */
export function readSheet(xml: string, sharedStrings: readonly string[]): readonly TableRow[] {
  const rows: TableRow[] = [];

  for (const rowMatch of xml.matchAll(/<row\b([^>]*)>([\s\S]*?)<\/row>/g)) {
    const attributes = rowMatch[1] ?? '';
    const body = rowMatch[2] ?? '';
    const line = Number(attribute(`<row ${attributes}>`, 'r') ?? rows.length + 1);

    const fields: string[] = [];
    for (const cellMatch of body.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const cell = `<c ${cellMatch[1] ?? ''}>`;
      const column = columnIndex(attribute(cell, 'r') ?? '');
      const value = cellValue(cellMatch[2] ?? '', attribute(cell, 't'), sharedStrings);

      const at = column === -1 ? fields.length : column;
      while (fields.length < at) fields.push('');
      fields[at] = value;
    }

    rows.push({ line, fields });
  }
  return rows;
}

function cellValue(
  body: string,
  type: string | null,
  sharedStrings: readonly string[],
): string {
  if (type === 's') {
    const index = Number(firstTag(body, 'v') ?? '');
    return sharedStrings[index] ?? '';
  }
  if (type === 'inlineStr') {
    return [...body.matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)]
      .map((run) => decodeXml(run[1] ?? ''))
      .join('');
  }
  // `str` is a formula result, `b` a boolean, `e` an error, and no type at all
  // is a number — all of which live in `<v>` and are used as written.
  return decodeXml(firstTag(body, 'v') ?? '').trim();
}

function firstTag(body: string, tag: string): string | null {
  const match = new RegExp(`<${tag}\\b[^>]*>([\\s\\S]*?)</${tag}>`).exec(body);
  return match === null ? null : (match[1] ?? '');
}

function attribute(tag: string, name: string): string | null {
  const match = new RegExp(`\\b${name.replace(':', '\\:')}="([^"]*)"`).exec(tag);
  return match === null ? null : (match[1] ?? '');
}

/** `C7` → 2. Letters only; the row number is not this function's business. */
export function columnIndex(reference: string): number {
  const letters = /^([A-Z]+)/.exec(reference.toUpperCase());
  if (letters === null) return -1;

  let index = 0;
  for (const letter of letters[1] as string) {
    index = index * 26 + (letter.charCodeAt(0) - 64);
  }
  return index - 1;
}

const ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};

function decodeXml(value: string): string {
  return value.replace(/&(#x?[0-9a-fA-F]+|\w+);/g, (whole, entity: string) => {
    if (entity.startsWith('#x') || entity.startsWith('#X')) {
      return String.fromCodePoint(Number.parseInt(entity.slice(2), 16));
    }
    if (entity.startsWith('#')) return String.fromCodePoint(Number(entity.slice(1)));
    return ENTITIES[entity.toLowerCase()] ?? whole;
  });
}

function text(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}
